/**
 * session/rival — the impure glue between the pure core/rival helpers and the live
 * world (rival-arc.md). No Phaser, no persistence: GameSession owns the RivalState field
 * + save; these functions choose the rival, mint their party as real instances, record
 * warm shared history, and mint the two rival beats. Kept out of gameSession.ts so the
 * session verbs stay thin delegators.
 */
import type {
  Beat,
  ChatChannel,
  MemoryRecord,
  PokemonInstance,
  RivalState,
  SimTrainer,
  SpeciesData,
} from '@/shared';
import type { DataIndex } from '@/data';
import { makeInstance } from '@/society';
import {
  makeRng,
  hashString,
  mirrorStarter,
  rivalBadgesForPlayer,
  rivalSparLevel,
  buildRivalPartySpec,
  type AceLineStep,
} from '@/core';

/**
 * Deterministically pin the rival: the warmest `lod:'regular'` in the (seed-deterministic,
 * persisted) roster becomes "your first friend on the circuit". Warmth-then-id ordering is
 * stable for a given save, so roster growth never reshuffles them (rival-arc.md §Design).
 * Their starter mirrors the player's via the type-triangle; badges start one behind.
 * Returns null only if there are no regulars at all (a warm no-op — no rival this session).
 */
export function chooseRival(
  trainers: readonly SimTrainer[],
  playerStarterId: string,
  playerBadges: number,
  now: number,
): RivalState | null {
  const regulars = trainers.filter((t) => t.lod === 'regular');
  if (regulars.length === 0) return null;
  const pick = [...regulars].sort((a, b) => {
    const w = b.personality.warmth - a.personality.warmth;
    return w !== 0 ? w : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  })[0]!;
  return {
    trainerId: pick.id,
    starterSpeciesId: mirrorStarter(playerStarterId),
    badges: rivalBadgesForPlayer(playerBadges),
    sparWins: 0,
    sparLosses: 0,
    lastGreetedBadge: -1, // no tier greeted yet → the first cameo fires
    firstMetAt: now,
  };
}

/**
 * Build the mirrored starter's level-up evolution line as ordered {speciesId, minLevel}
 * steps (base at minLevel 0), following the first level-up branch at each step. Pure walk
 * over the dataset; stops on a missing species or a non-level-up-only branch. This is what
 * lets the rival's ace evolve across the arc (charmander → charmeleon → charizard).
 */
export function aceLineFor(index: DataIndex, baseSpeciesId: string): AceLineStep[] {
  const line: AceLineStep[] = [];
  const seen = new Set<string>();
  let cur: SpeciesData | undefined = index.speciesById(baseSpeciesId);
  let minLevel = 0;
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    line.push({ speciesId: cur.id, minLevel });
    const next = cur.evolutions.find((e) => e.trigger === 'level-up' && e.minLevel != null);
    if (!next) break;
    minLevel = next.minLevel!;
    cur = index.speciesById(next.toSpeciesId);
  }
  return line;
}

/** Warm companions for the rival's spar party — their own society team's species. */
export function companionPoolFor(rival: SimTrainer | undefined): string[] {
  if (!rival) return [];
  return rival.team.map((m) => m.speciesId);
}

/**
 * Mint the rival's spar party into faithful, battle-ready instances (real stats + hydrated
 * PP + full HP), mirroring GymScene.buildLeaderParty / GameSession.friendlyParty. The SPEC
 * (which species, what level) is decided by the pure core builder, so it's deterministic
 * per (rival, badge tier, player level) — a stable, fun, close match. Species the dataset
 * lacks are skipped rather than crashing a thin build.
 */
export function buildRivalParty(args: {
  readonly index: DataIndex;
  readonly seed: number;
  readonly rival: RivalState;
  readonly rivalTrainer: SimTrainer | undefined;
  readonly leadLevel: number;
}): PokemonInstance[] {
  const spec = buildRivalPartySpec({
    aceLine: aceLineFor(args.index, args.rival.starterSpeciesId),
    leadLevel: args.leadLevel,
    companionPool: companionPoolFor(args.rivalTrainer),
    badgeTier: args.rival.badges,
  });
  const rng = makeRng(
    (args.seed ^ hashString(`rival|${args.rival.trainerId}|${args.rival.badges}`)) >>> 0,
  );
  const owner = {
    id: `rival-${args.rival.trainerId}`,
    name: args.rivalTrainer?.username ?? 'Your rival',
  };
  const party: PokemonInstance[] = [];
  for (const member of spec) {
    const species = args.index.speciesById(member.speciesId);
    if (!species) continue;
    const mon = makeInstance(rng, species, member.level, owner);
    mon.currentHp = mon.maxHp;
    mon.status = 'none';
    mon.statusTurns = 0;
    mon.moves = mon.moves.map((slot) => {
      if (slot.maxPp > 0) return { ...slot, pp: slot.maxPp };
      const maxPp = args.index.moveById(slot.moveId)?.pp ?? 5;
      return { ...slot, pp: maxPp, maxPp };
    });
    party.push(mon);
  }
  return party;
}

/** The player-level a spar scales to: the lead team mon's level (min 5). */
export function sparLeadLevel(team: readonly PokemonInstance[]): number {
  return rivalSparLevel(team[0]?.level ?? 5);
}

/**
 * Fold a spar result into shared history: bump the warm tally + drop a memory the voice
 * layer can call back to. A loss is recorded exactly as warmly as a win (never a penalty).
 * Mutates the passed RivalState + SimTrainer in place; the caller persists.
 */
export function recordSpar(
  rival: RivalState,
  rivalTrainer: SimTrainer | undefined,
  won: boolean,
  now: number,
): void {
  if (won) rival.sparWins += 1;
  else rival.sparLosses += 1;
  if (rivalTrainer) {
    const total = rival.sparWins + rival.sparLosses;
    const mem: MemoryRecord = {
      id: `mem-rival-spar-${total}-${now}`,
      aboutId: 'player',
      kind: 'battled',
      summary: won
        ? 'we had a friendly spar and they got me — loved it'
        : 'we had a friendly spar, closest match yet',
      at: now,
    };
    rivalTrainer.memories.push(mem);
    if (rivalTrainer.memories.length > 24) {
      rivalTrainer.memories.splice(0, rivalTrainer.memories.length - 24);
    }
    // A spar only ever warms the friendship (never down — triple contract).
    rivalTrainer.friendshipWithPlayer = Math.min(100, rivalTrainer.friendshipWithPlayer + 2);
  }
}

// ── beats (the voice layer phrases these; {giver} resolves from data.giverId) ─────

/**
 * The rival rooting for the player — win or lose. `subjectTrainerId`/`data.giverId` are the
 * rival so the template's {giver} names them and {player} names the player. Warm by
 * construction (see the rival-cheer template bank).
 */
export function rivalCheerBeat(rivalId: string, channel: ChatChannel, at: number): Beat {
  return {
    id: `rival-cheer-${rivalId}-${at}`,
    kind: 'rival-cheer',
    priority: 'normal',
    subjectTrainerId: rivalId,
    involvedTrainerIds: ['player'],
    channel,
    data: { giverId: rivalId, warm: true },
    createdAt: at,
  };
}

/**
 * The rival earned a badge — always JUST behind you, framed as happy-for-both-of-you
 * (never "catching up to beat you"). Emitted at most once per badge tier by the caller.
 */
export function rivalMilestoneBeat(
  rivalId: string,
  rivalBadges: number,
  channel: ChatChannel,
  at: number,
): Beat {
  return {
    id: `rival-milestone-${rivalId}-${rivalBadges}-${at}`,
    kind: 'rival-milestone',
    priority: 'normal',
    subjectTrainerId: rivalId,
    involvedTrainerIds: ['player'],
    channel,
    data: { giverId: rivalId, badges: rivalBadges, warm: true },
    createdAt: at,
  };
}
